import React, { createContext, useContext, useState, useEffect } from "react";
import ProfileCompletionStatusApi from "../api_calls/ProfileCompletionStatusApi";
import ProfileDetails from "../api_calls/ProfileDetailsApi";
import StorageKeys from "../constants/storageKeys";
import { clearCachedAccessTokenForServiceWorker } from "../utils/tokenIndexedDb";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem(StorageKeys.USER);
    // console.log("AuthProvider initializing. Stored user:", storedUser);

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // Only re-check profile completion if user is logged in & incomplete
        if (
          (parsedUser.user_id || parsedUser.token) &&
          parsedUser.profile_complete !== "yes"
        ) {
          console.log(
            "Profile incomplete. Re-checking latest profile status...",
          );

          ProfileCompletionStatusApi.fetchProfileCompletionStatus(
            parsedUser.user_id,
          )
            .then((latestProfileData) => {
              if (latestProfileData?.message === "yes") {
                console.log("Profile now complete. Updating local user state.");

                const newUserData = {
                  ...parsedUser,
                  profile_complete: "yes",
                };

                setUser(newUserData);
                localStorage.setItem(StorageKeys.USER, JSON.stringify(newUserData));
                // ❌ no reload
              }
            })
            .catch((error) => {
              console.error(
                "Failed to re-verify profile status via API:",
                error,
              );
            });
        }
      } catch (e) {
        console.error("Invalid stored user. Clearing storage.", e);
        localStorage.removeItem(StorageKeys.USER);
      }
    }

    setIsLoading(false);
  }, []);

  // Store user data and save in localStorage
  const storeUserData = (userData) => {
    console.log("Storing user data:", userData);
    setUser(userData);
    localStorage.setItem(StorageKeys.USER, JSON.stringify(userData));
  };

  // Update specific user fields safely
  const updateUserProfile = (updates) => {
    if (!user) return;

    const newUserData = { ...user, ...updates };
    setUser(newUserData);
    localStorage.setItem(StorageKeys.USER, JSON.stringify(newUserData));
  };

  // Bumped whenever the user's profile photo changes, so anything showing it
  // (e.g. the sidebar avatar) can re-fetch it live, without a page reload.
  const [profilePhotoVersion, setProfilePhotoVersion] = useState(0);
  const notifyProfilePhotoChanged = () => setProfilePhotoVersion((v) => v + 1);

  // Per-item mandatory-profile-data checklist (profile photo / gallery photos /
  // about me). Shared here so any component can trigger a re-check after a
  // save — e.g. so ProfileIncompleteBanner updates live, with no page reload.
  const [profileChecklist, setProfileChecklist] = useState(null);

  const refreshProfileChecklist = async () => {
    if (!user?.user_id) return;

    const profileData = await ProfileDetails.fetchProfileDetails(
      user.user_id,
    );
    if (!profileData) return;

    const checklist = {
      hasProfilePhoto: Boolean(profileData.has_profile_photo),
      hasGalleryPhotos: (profileData.photos?.length || 0) >= 3,
      hasAboutMe: Boolean(profileData.aboutMe?.trim()),
    };

    setProfileChecklist(checklist);
    updateUserProfile({
      profile_complete:
        checklist.hasProfilePhoto &&
        checklist.hasGalleryPhotos &&
        checklist.hasAboutMe
          ? "yes"
          : "no",
    });
  };

  // Logout
  const logout = () => {
    setUser(null);
    // Clear only the session's own data — a blanket localStorage.clear()
    // would also wipe unrelated saved state like cookie consent.
    localStorage.removeItem(StorageKeys.USER);
    localStorage.removeItem(StorageKeys.ACCESS_TOKEN);
    localStorage.removeItem(StorageKeys.REFRESH_TOKEN);
    sessionStorage.clear();
    clearCachedAccessTokenForServiceWorker();
    window.location.href = "/auth/login-page";
  };

  // ✅ Stable auth signals
  const isLoggedIn = Boolean(user?.user_id || user?.token);
  const isVerified = user?.is_verified === "yes";
  const profileIsComplete = user?.profile_complete === "yes";

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isLoggedIn,
        isVerified,
        profileIsComplete,
        storeUserData,
        logout,
        updateUserProfile,
        profileChecklist,
        refreshProfileChecklist,
        profilePhotoVersion,
        notifyProfilePhotoChanged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
